import { isVideoExportRecordingActive } from "@/animation/video-generator/exportActivity";
import { isTileLayerFallbackTransitionsEnabled } from "@/feature-flags/non-hook/non-hook";
import {
  getCurrentAnimationFps,
  isAnimationPlaying,
  isAnimationSliderScrubbing,
} from "@/models/time-control/animationPlaybackActivity";

export const DEFAULT_MAX_TRANSITION_ANIMATION_FPS = 5;

export type TileZoomDirection = "in" | "out";

export type TileLayerTransitionRuntime = {
  isAnimating: boolean;
  animationFps: number | null;
  isRecording: boolean;
  isScrubbing: boolean;
  isMapZooming?: boolean;
};

export type TileLayerTransitionState = {
  lastObservedTimeKey: string | null;
  lastObservedViewKey: string | null;
  lastObservedZoomValue: number | null;
  pendingTimeTransition: boolean;
  pendingZoomDirection: TileZoomDirection | null;
};

export type TileLayerTransitionObservation = {
  timeKey: string;
  viewKey: string;
  zoom: number;
  runtime: Pick<TileLayerTransitionRuntime, "isAnimating" | "isRecording" | "isScrubbing">;
  isSettled?: boolean;
};

export type TileLayerTransitionUpdateResult = {
  startedTimeTransition: boolean;
  startedZoomTransition: boolean;
  zoomDirection: TileZoomDirection | null;
};

export type TileLayerTransitionSettledCheckObservation = {
  timeKey: string;
  viewKey: string;
  zoom: number;
};

type TileLayerFallbackTransitionFeatureOptions = {
  tileLayerFallbackTransitionsEnabled?: boolean;
};

function areTileLayerFallbackTransitionsEnabled(options?: TileLayerFallbackTransitionFeatureOptions): boolean {
  return options?.tileLayerFallbackTransitionsEnabled ?? isTileLayerFallbackTransitionsEnabled();
}

export function getCurrentTileLayerTransitionRuntime(map?: {
  isZooming?: () => boolean;
}): TileLayerTransitionRuntime {
  return {
    isAnimating: isAnimationPlaying(),
    animationFps: getCurrentAnimationFps(),
    isRecording: isVideoExportRecordingActive(),
    isScrubbing: isAnimationSliderScrubbing(),
    isMapZooming: map?.isZooming?.() ?? false,
  };
}

export function shouldDisableTileLayerTransitions(
  runtime: Pick<TileLayerTransitionRuntime, "isAnimating" | "animationFps" | "isRecording" | "isScrubbing">,
  maxTransitionAnimationFps = DEFAULT_MAX_TRANSITION_ANIMATION_FPS,
  options?: TileLayerFallbackTransitionFeatureOptions,
): boolean {
  return (
    !areTileLayerFallbackTransitionsEnabled(options) ||
    runtime.isRecording ||
    runtime.isScrubbing ||
    (runtime.isAnimating && (runtime.animationFps ?? 0) > maxTransitionAnimationFps)
  );
}

export function shouldAllowLowQualityFallbackTiles(
  runtime: Pick<TileLayerTransitionRuntime, "isAnimating" | "isRecording" | "isScrubbing" | "isMapZooming">,
  state: Pick<TileLayerTransitionState, "pendingTimeTransition" | "pendingZoomDirection">,
  options?: TileLayerFallbackTransitionFeatureOptions,
): boolean {
  return (
    areTileLayerFallbackTransitionsEnabled(options) &&
    !runtime.isRecording &&
    !runtime.isAnimating &&
    !runtime.isScrubbing &&
    (!runtime.isMapZooming || state.pendingZoomDirection === "out") &&
    !state.pendingTimeTransition &&
    state.pendingZoomDirection !== "in"
  );
}

export function shouldRequestLowQualityBackingTiles(
  runtime: Pick<TileLayerTransitionRuntime, "isAnimating" | "isRecording" | "isScrubbing">,
  state: Pick<TileLayerTransitionState, "pendingTimeTransition" | "pendingZoomDirection">,
  options?: TileLayerFallbackTransitionFeatureOptions,
): boolean {
  return (
    areTileLayerFallbackTransitionsEnabled(options) &&
    !runtime.isRecording &&
    !runtime.isAnimating &&
    !runtime.isScrubbing &&
    !state.pendingTimeTransition &&
    state.pendingZoomDirection !== "in"
  );
}

export function shouldRenderRetainedFallbackTiles(
  runtime: Pick<TileLayerTransitionRuntime, "isAnimating" | "isRecording" | "isScrubbing">,
  options?: TileLayerFallbackTransitionFeatureOptions,
): boolean {
  return (
    areTileLayerFallbackTransitionsEnabled(options) &&
    !runtime.isRecording &&
    !runtime.isAnimating &&
    !runtime.isScrubbing
  );
}

export function syncTileLayerTransitionState(
  state: TileLayerTransitionState,
  observation: TileLayerTransitionObservation,
  options?: TileLayerFallbackTransitionFeatureOptions,
): TileLayerTransitionUpdateResult {
  const result: TileLayerTransitionUpdateResult = {
    startedTimeTransition: false,
    startedZoomTransition: false,
    zoomDirection: null,
  };
  const { runtime } = observation;
  const shouldClearPending =
    !areTileLayerFallbackTransitionsEnabled(options) ||
    runtime.isAnimating ||
    runtime.isRecording ||
    runtime.isScrubbing;

  if (shouldClearPending) {
    state.pendingTimeTransition = false;
    state.pendingZoomDirection = null;
  } else {
    if (
      state.lastObservedTimeKey != null &&
      state.lastObservedTimeKey !== observation.timeKey &&
      state.lastObservedViewKey === observation.viewKey
    ) {
      result.startedTimeTransition = true;
      state.pendingTimeTransition = true;
    }

    if (state.lastObservedZoomValue != null && observation.zoom !== state.lastObservedZoomValue) {
      const zoomDirection = observation.zoom > state.lastObservedZoomValue ? "in" : "out";
      result.startedZoomTransition = true;
      result.zoomDirection = zoomDirection;
      state.pendingZoomDirection = zoomDirection;
    }
  }

  if (observation.isSettled && (state.pendingTimeTransition || state.pendingZoomDirection)) {
    state.pendingTimeTransition = false;
    state.pendingZoomDirection = null;
  }

  state.lastObservedTimeKey = observation.timeKey;
  state.lastObservedViewKey = observation.viewKey;
  state.lastObservedZoomValue = observation.zoom;

  return result;
}

export function shouldCheckTileLayerTransitionSettled(
  state: TileLayerTransitionState,
  observation: TileLayerTransitionSettledCheckObservation,
  options: { trackZoomOut?: boolean } & TileLayerFallbackTransitionFeatureOptions = {},
): boolean {
  if (!areTileLayerFallbackTransitionsEnabled(options)) {
    return false;
  }

  const trackZoomOut = options.trackZoomOut ?? true;
  const hasPendingTransition = state.pendingTimeTransition || state.pendingZoomDirection != null;
  const willStartTimeTransition =
    state.lastObservedTimeKey != null &&
    state.lastObservedTimeKey !== observation.timeKey &&
    state.lastObservedViewKey === observation.viewKey;
  const willStartZoomTransition =
    state.lastObservedZoomValue != null &&
    (trackZoomOut ? observation.zoom !== state.lastObservedZoomValue : observation.zoom > state.lastObservedZoomValue);

  return hasPendingTransition || willStartTimeTransition || willStartZoomTransition;
}
